import React from 'react';
import { Avatar, createTheme, Tabs, Tab, Box, Button, Typography, TextField, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Grid, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, IconButton } from '@mui/material';
import Client from '../Client';
import Config from '../ClientConfig';
import BackofficeReportsApi from './backofficeReportsApi';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
// npm install jspdf jspdf-autotable
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

let theme = createTheme();
const objectToCsv = function (data) {
  const csvRows = [];
  const headers = Object.keys(data[0]);
  csvRows.push(headers.join(','));
  for (const row of data) {
    const values = headers.map(header => {
      const val = row[header];
      return `"${val}"`;
    });
    csvRows.push(values.join(','));
  }
  return csvRows.join('\n');
};
const stripDiacritics = (text = "") => {
  return text
    .normalize("NFD")                 // separate accent from letter
    .replace(/[\u0300-\u036f]/g, "")  // remove all accents
    .replace(/ș/g, "s")
    .replace(/Ș/g, "S")
    .replace(/ț/g, "t")
    .replace(/Ț/g, "T");
};

const compactFieldSx = {
  '& .MuiInputBase-root': {
    minHeight: 34,
    borderRadius: '12px',
  },
  '& .MuiInputBase-input': {
    fontSize: {
      xs: '16px',
      sm: '0.84rem',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: {
      xs: '0.78rem',
      sm: '0.84rem',
    },
  },
};
const compactButtonSx = {
  height: 32,
  borderRadius: '11px',
  textTransform: 'none',
  fontWeight: 700,
};
const compactTabPaperSx = {
  flexShrink: 0,
  borderRadius: '12px',
  border: '1px solid rgba(15, 23, 42, 0.08)',
  overflow: 'hidden',
  mb: 0.75,
};
const compactPageSx = {
  width: '100%',
  height: '100%',
  maxHeight: '100%',
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
  bgcolor: 'background.default',
  display: 'flex',
  flexDirection: 'column',
};
const compactPanelSx = {
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};
const compactPaperSx = {
  p: {
    xs: 0.75,
    sm: 1,
  },
  height: '100%',
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '14px',
};
const compactTableContainerSx = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  borderRadius: '12px',
};
const compactTableSx = {
  '& .MuiTableCell-stickyHeader': {
    backgroundColor: '#f8fafc',
    zIndex: 3,
  },
  '& .MuiTableCell-root': {
    py: { xs: 0.4, sm: 0.55 },
    px: { xs: 0.45, sm: 0.75 },
    fontSize: { xs: '0.72rem', sm: '0.84rem' },
  },
};

class InventoryUI extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client('none'),
      tabIndex: 0,
      inventoryChecks: [],
      productEntries: [],
      inventoryData: {
        date: '',
        location: '',
        description: '',
        inventoryCheckLines_invCheck: []
      },
      exportDate: '',
      exportInvId: '',
      filterDate: '',
      selectedLocationFilter: '0',
      tabs: ['Inventory List', 'New Inventory'],
      selectedInventory: null,
      showDetailDialog: false,
      editDate: '',
      editMode: false,
      isSavingDate: false,
      location: 0,
      locations: [{ iref: 0, name: 'Select Location' }],
      lineEdits: {},            // { [lineIref]: val }
      savingLineIds: {},        // { [lineIref]: true }
      editingLineIds: {},       // { [lineIref]: true } -> controls visibility of Save button
      showAddProductDialog: false,
      addProductId: '',
      addQty: '',
      isAddingLine: false,
    };

    this.getData = this.getData.bind(this);
    this.selectLocation = this.selectLocation.bind(this);
    this.fetchInventoryChecks = this.fetchInventoryChecks.bind(this);
  }

  componentDidMount() {
    this.fetchInventoryChecks();
    this.getData();
  }

  fetchInventoryChecks = (locationId = null) => {
    const { filterDate } = this.state;
    const queryData = { limit: 100 };
    if (locationId) queryData.location = locationId;
    if (filterDate) queryData.date = filterDate;
    let data = this.state.client.getData(Config.API_URL_INVENTORY_CHECK, queryData);
    if (data) {
      let checks = [];
      if (Array.isArray(data)) {
        checks = data;
      } else if (Array.isArray(data.inventoryCheck)) {
        checks = data.inventoryCheck;
      } else if (data.inventoryCheck) {
        checks = [data.inventoryCheck];
      }
      this.setState({ inventoryChecks: checks });
    }
  };

  getData() {
    let queryData = {
      askQuantity: 1,
      isPurchased: 1,
      status: 2,
      orderBy_name: 1
    };
    let data = this.state.client.getData(Config.API_URL_PRODUCT, queryData);
    if (data) {
      const productEntries = Object.keys(data.products || {}).map((key) => {
        const fData = data.products[key];
        return {
          productId: fData.iref,
          productName: fData.name,
          relation: fData.relation || '-',
          unit: fData.unitType,
          quantity: '',
          reactKey: `${fData.iref}-${key}`
        };
      });
      this.setState({
        products: data,
        productEntries: productEntries
      });
    } else {
      alert('Failed to load products.');
    }

    let locations = BackofficeReportsApi.getLocations(this.state.client);
    this.setState({ locations: locations.locations });
  }

  handleLocationFilterChange = (e) => {
    const value = e.target.value;
    this.setState({ selectedLocationFilter: value }, () => {
      this.fetchInventoryChecks(value && value !== '0' ? value : null);
    });
  };

  selectLocation = (event) => {
    this.setState(
      { location: event.target.value },
      () => this.handleInventoryChange('location', event.target.value)
    );
  };

  handleChange = (event, newValue) => {
    this.setState({ tabIndex: newValue });
  };

  handleInventoryChange = (field, value) => {
    this.setState(prev => ({
      inventoryData: {
        ...prev.inventoryData,
        [field]: value
      }
    }));
  };

  handleQuantityChange = (index, value) => {
    const updated = [...this.state.productEntries];
    updated[index].quantity = value || 0;
    this.setState({ productEntries: updated });
  };

  handleLineQtyChange = (lineIref, value) => {
    this.setState(prev => ({
      lineEdits: { ...prev.lineEdits, [lineIref]: value }
    }));
  };

  toggleEditLine = (lineIref, initialVal) => {
    this.setState(prev => ({
      editingLineIds: {
        ...prev.editingLineIds,
        [lineIref]: !prev.editingLineIds[lineIref]
      },
      // Seed the input with current quantity when entering edit mode
      lineEdits: !prev.editingLineIds[lineIref]
        ? { ...prev.lineEdits, [lineIref]: initialVal }
        : prev.lineEdits
    }));
  };

  saveLineQty = (line, unit) => {
    const selected = this.state.selectedInventory;
    if (!selected) return;

    const base = Config.API_URL_INVENTORY_CHECK;
    const url = `${base}${base.endsWith('/') ? '' : '/'}update-line-qty/`;
    const lineIref = line?.iref ?? line?.line_iref ?? line?.id;
    const productId =
      (line?.product && typeof line.product === 'object')
        ? line.product.iref
        : (line?.product ?? line?.product_id ?? line?.productId);
    const key = String(line?.iref ?? lineIref ?? productId ?? '');
    if (!key) {
      alert('Cannot update: missing line id and product id.');
      return;
    }
    const raw = this.state.lineEdits[line?.iref] ?? this.state.lineEdits[key];
    if (raw === undefined || raw === null || raw === '') {
      alert('Please enter a quantity first.');
      return;
    }

    const rawStr = String(raw).replace(',', '.');
    if (unit === 0) {
      if (!/^\d+$/.test(rawStr)) {
        alert('Quantity must be an integer for pcs.');
        return;
      }
    } else {
      if (!/^\d*\.?\d{0,3}$/.test(rawStr)) {
        alert('Quantity must be a number with up to 3 decimals.');
        return;
      }
    }

    const qty = parseFloat(rawStr);
    if (Number.isNaN(qty)) {
      alert('Invalid quantity.');
      return;
    }
    const savingKey = String(line?.iref ?? key);
    this.setState(prev => ({ savingLineIds: { ...prev.savingLineIds, [savingKey]: true } }));

    const payload = {
      iref: selected.iref,
      quantity: qty,
      ...(lineIref ? { line_iref: lineIref } : {}),
      ...(productId ? { product: productId } : {}),
    };

    const res = this.state.client.pushData(url, payload);
    this.setState(prev => ({ savingLineIds: { ...prev.savingLineIds, [savingKey]: false } }));

    if (res && (res.iref || res.line_iref || res.id)) {
      this.setState(prev => {
        const updatedSelected = { ...prev.selectedInventory };

        updatedSelected.inventoryCheckLines_invCheck = (updatedSelected.inventoryCheckLines_invCheck || []).map(l => {
          const lLineIref = l?.iref ?? l?.line_iref ?? l?.id;
          const lProductId =
            (l?.product && typeof l.product === 'object')
              ? l.product.iref
              : (l?.product ?? l?.product_id ?? l?.productId);

          const lKey = String(lLineIref ?? lProductId ?? '');

          return lKey === key ? { ...l, quantity: qty } : l;
        });

        const updatedList = (prev.inventoryChecks || []).map(it => {
          if (String(it.iref || it.id) !== String(selected.iref)) return it;

          return {
            ...it,
            inventoryCheckLines_invCheck: (it.inventoryCheckLines_invCheck || []).map(l => {
              const lLineIref = l?.iref ?? l?.line_iref ?? l?.id;
              const lProductId =
                (l?.product && typeof l.product === 'object')
                  ? l.product.iref
                  : (l?.product ?? l?.product_id ?? l?.productId);

              const lKey = String(lLineIref ?? lProductId ?? '');
              return lKey === key ? { ...l, quantity: qty } : l;
            })
          };
        });

        return {
          selectedInventory: updatedSelected,
          inventoryChecks: updatedList,
          editingLineIds: { ...prev.editingLineIds, [savingKey]: false },
        };
      });
    } else {
      const msg = res?.detail || res?.message || 'Update failed.';
      alert(msg);
    }
  };

  handleSubmit = () => {
    const { inventoryData, productEntries } = this.state;
    if (!inventoryData.date || !inventoryData.location) {
      alert('Please fill in both Date and Location before submitting.');
      return;
    }
    const filteredProducts = productEntries
      .filter(p => p.quantity && !isNaN(p.quantity) && parseFloat(p.quantity) > 0)
      .map(p => ({
        product: p.productId,
        unit: p.unit,
        quantity: parseFloat(p.quantity),
        accountingQty: 0.0,
      }));

    if (filteredProducts.length === 0) {
      alert('Please enter at least one product line with quantity > 0.');
      return;
    }

    const payload = { ...inventoryData, inventoryCheckLines_invCheck: filteredProducts };
    const result = this.state.client.pushData(Config.API_URL_INVENTORY_CHECK, payload);
    if (result && result.iref) {
      alert('Inventory check saved!');
      this.setState(prev => ({
        inventoryData: { date: '', location: '', description: '' },
        productEntries: prev.productEntries.map(p => ({ ...p, quantity: '' })),
        location: 0
      }));
      this.setState({ tabIndex: 0 });
      this.fetchInventoryChecks();
    } else {
      console.error('Save failed', result);
      const errorMessage = result?.message || 'Save failed.';
      alert(errorMessage);
    }
  };

  openInventoryDetail = (inv) => {
    this.setState({
      selectedInventory: inv,
      location: inv.location,
      date: inv.date,
      editDate: inv.date || '',
      editMode: false,
      showDetailDialog: true,
      lineEdits: {},
      savingLineIds: {},
      editingLineIds: {},
    });
  };

  closeInventoryDetail = () => {
    this.setState({ showDetailDialog: false, selectedInventory: null });
  };

  onDownload = () => {
    const { selectedInventory, productEntries } = this.state;
    if (!selectedInventory || !Array.isArray(selectedInventory.inventoryCheckLines_invCheck)) {
      console.error('Missing data for CSV export.');
      return;
    }
    const invNumber = selectedInventory.iref || selectedInventory.id || '-';
    const linesWithNames = selectedInventory.inventoryCheckLines_invCheck.map(line => {
      const productInfo = productEntries.find(p => p.productId === line.product);
      return {
        Product: productInfo?.productName || line.product || '-',
        Quantity: line.quantity ?? '-',
        Unit: productInfo?.unit === 0 ? 'pcs' : productInfo?.unit === 1 ? 'kg' : '-',
      };
    });
    const link = document.createElement('a');
    link.download = 'locationInventory_' + this.state.location.name + '_' + this.state.date + '.csv';
    const csvData = objectToCsv(linesWithNames);
    let csvHeader =
      'Location Inventory\n' +
      'Date: ' + this.state.date + '\n' +
      'Location: ' + this.state.location.name + '\n' +
      'Inventory Check: ' + invNumber;
    let csv = "\uFEFF" + csvHeader + "\n" + csvData;
    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    link.click();
  };

  handleExportAll = () => {
    const { exportDate, client, exportInvId, selectedLocationFilter } = this.state;

    const invId = String(exportInvId || '').trim();
    const hasLoc = selectedLocationFilter && String(selectedLocationFilter) !== '0';

    // If no inv id, we rely on date (your UI already calls it Filter/Export date)
    if (!invId && !exportDate) {
      alert('Please select a date or enter an Inventory Check ID.');
      return;
    }

    // Build query
    const query = { limit: 5000 };

    // If user typed an inventory check id => most reliable path
    if (invId) {
      query.iref = invId;
    } else {
      query.date = exportDate;
      if (hasLoc) query.location = selectedLocationFilter;
    }

    const data = client.getData(Config.API_URL_INVENTORY_CHECK, query);

    // Normalize response -> checks[]
    let checks = [];
    if (Array.isArray(data)) checks = data;
    else if (Array.isArray(data?.inventoryCheck)) checks = data.inventoryCheck;
    else if (data?.inventoryCheck) checks = [data.inventoryCheck];

    if (!checks.length) {
      alert('No inventory checks found for the selected filter.');
      return;
    }

    // If exporting by ID, try to narrow to exact iref match (defensive)
    if (invId) {
      const exact = checks.filter(c => String(c.iref || c.id) === String(invId));
      if (exact.length) checks = exact;
    }

    // If exporting by date+location and multiple checks exist, pick ONLY the latest
    // (highest iref/id is typically newest)
    if (!invId && hasLoc && checks.length > 1) {
      checks.sort((a, b) => Number(b.iref || b.id || 0) - Number(a.iref || a.id || 0));
      checks = [checks[0]];
    }

    // Build CSV rows
    const rows = [];

    for (const ic of checks) {
      const invNumber = ic.iref || ic.id || '-';
      const invDate = ic.date || '';
      const locationName = ic.location?.name || '-';
      const locationRelation = ic.location?.extRelation || '-';

      const lines = ic.inventoryCheckLines_invCheck || [];
      for (const l of lines) {
        const product = l.productDetails || l.product || {};
        const prodName =
          product.name ||
          this.state.productEntries.find(p => String(p.productId) === String(l.product))?.productName ||
          '-';
        const prodRelation =
          product.relation ||
          this.state.productEntries.find(p => String(p.productId) === String(l.product))?.relation ||
          '-';

        rows.push({
          inventory_iref: invNumber,
          inventory_date: invDate,
          location_name: locationName,
          location_relation: locationRelation,
          line_iref: l.iref || '-',
          product_name: prodName,
          product_relation: prodRelation,
          quantity: l.quantity ?? 0,
        });
      }
    }

    if (!rows.length) {
      alert('No inventory lines found for export.');
      return;
    }

    // File name: use ID if provided, else date (+location if chosen)
    const filename = invId
      ? `inventory_export_${invId}.csv`
      : hasLoc
        ? `inventory_export_${exportDate}_loc_${selectedLocationFilter}.csv`
        : `inventory_export_${exportDate}.csv`;

    let csv = "\uFEFF" + objectToCsv(rows);
    const link = document.createElement('a');
    link.download = filename;
    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    link.click();
  };

  handleExportProductsPDF = () => {
    const { productEntries } = this.state;
    const locationName = this.state.locations.find(l => l.iref === this.state.inventoryData.location)?.name || "";
    const inventoryDate = this.state.inventoryData.date || "";

    if (!productEntries.length) {
      alert("No products available.");
      return;
    }

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "A4"
    });

    doc.setFontSize(16);
    doc.text("Inventory List", 40, 40);
    doc.setFontSize(12);
    doc.text("Location: " + stripDiacritics(locationName || ""), 40, 60);
    doc.setFontSize(11);
    doc.text("Date: " + (inventoryDate || ""), 40, 80);

    const rows = productEntries.map(p => [
      stripDiacritics(p.productName),
      p.unit === 0 ? "pc" : p.unit === 1 ? "kg" : "-",
      ""
    ]);

    autoTable(doc, {
      startY: 90,
      head: [["Product", "Unit", "Qty"]],
      body: rows,
      theme: "grid",
      styles: {
        cellPadding: 3,
        fontSize: 9,
        lineHeight: 1.0,
        overflow: "ellipsize",
        cellWidth: "wrap",
      },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: 20,
        fontStyle: "bold"
      },
      columnStyles: {
        0: { cellWidth: 200 },
        1: { cellWidth: 40 },
        2: { cellWidth: 120 }
      }
    });

    // ---- ADD PAGE NUMBERS HERE ----
    const pageCount = doc.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() - 60, // right aligned
        doc.internal.pageSize.getHeight() - 20 // bottom margin
      );
    }

    doc.save("inventory_product_list.pdf");
  };

  handleSaveDate = () => {
    const { selectedInventory, editDate } = this.state;
    if (!selectedInventory) return;

    const toYMD = (val) => {
      if (!val) return '';
      if (typeof val === 'string') return val;
      if (val && typeof val.format === 'function') return val.format('YYYY-MM-DD');
      try { const d = new Date(val); return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0,10); } catch { return ''; }
    };

    const dateStr = toYMD(editDate);
    if (!dateStr) {
      alert('Please select a valid date.');
      return;
    }

    this.setState({ isSavingDate: true });

    const iref = selectedInventory.iref;
    const base = Config.API_URL_INVENTORY_CHECK;
    const url  = `${base}${base.endsWith('/') ? '' : '/'}update-date/`;

    const payload = { iref, date: dateStr };
    const res = this.state.client.pushData(url, payload);

    if (res && res.detail && String(res.detail).toLowerCase().includes('not found')) {
      alert('Could not update: record not found.');
      this.setState({ isSavingDate: false });
      return;
    }
    if (res && res.errors) {
      alert(`Could not update: ${JSON.stringify(res.errors)}`);
      this.setState({ isSavingDate: false });
      return;
    }

    this.setState(prev => {
      const updatedList = (prev.inventoryChecks || []).map(it =>
        String(it.iref || it.id) === String(iref) ? { ...it, date: dateStr } : it
      );
      return {
        inventoryChecks: updatedList,
        selectedInventory: { ...(prev.selectedInventory || {}), date: dateStr },
        isSavingDate: false,
        editMode: false,
      };
    });
  };

  renderInventoryList = () => {
    let locations = [{ iref: 0, name: 'Select Location' }];
    if (this.state.locations) {
      Object.keys(this.state.locations).forEach(key => {
        let fData = this.state.locations[key];
        let myData = { iref: fData.iref, name: fData.name };
        locations.push(myData);
      });
    }
    return (
      <Paper elevation={1} sx={compactPaperSx}>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.75, sm: 1 },
              flexWrap: 'wrap',
              pb: 0.75,
              '& .MuiButton-root': compactButtonSx,
            }}
          >
            <TextField
              id="select-location"
              label="Select Location"
              select
              variant="standard"
              size="small"
              sx={{
                ...compactFieldSx,
                width: {
                  xs: '100%',
                  sm: 220,
                },
                pt: 0,
              }}
              value={this.state.selectedLocationFilter || '0'}
              onChange={this.handleLocationFilterChange}
            >
              {locations.map((option) => (
                <MenuItem key={option.iref} value={option.iref}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Filter / Export date"
                value={this.state.filterDate ? dayjs(this.state.filterDate) : null}
                onChange={(newValue) => {
                  const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                  this.setState(
                    { filterDate: formatted, exportDate: formatted },
                    () => this.fetchInventoryChecks(this.state.selectedLocationFilter || null)
                  );
                }}
                inputFormat="DD.MM.YYYY"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    sx={{
                      ...compactFieldSx,
                      width: {
                        xs: 'calc(50% - 6px)',
                        sm: 180,
                      },
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </LocalizationProvider>

            <TextField
              label="Inventory Check ID"
              variant="standard"
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                ...compactFieldSx,
                width: {
                  xs: 'calc(50% - 6px)',
                  sm: 170,
                },
              }}
              value={this.state.exportInvId || ""}
              onChange={(e) => this.setState({ exportInvId: e.target.value })}
            />

            <Tooltip title="Export by Inventory Check ID (exact). If empty: exports by date, and if a location is selected exports the latest check for that location/date.">
              <Button
                variant="contained"
                size="small"
                onClick={this.handleExportAll}
                startIcon={<FileDownloadIcon />}
              >
                Export
              </Button>
            </Tooltip>
            <Button
              variant="text"
              size="small"
              sx={{ paddingTop: 2 }}
              onClick={() => this.setState({ exportDate: '', filterDate: '' }, () => {
                const loc = (this.state.selectedLocationFilter && this.state.selectedLocationFilter !== '0')
                  ? this.state.selectedLocationFilter
                  : null;
                this.fetchInventoryChecks(loc);
              })}
            >
              Clear date
            </Button>
          </Box>

          <TableContainer component={Paper} sx={compactTableContainerSx}>
            <Table stickyHeader size="small" sx={compactTableSx}>
              <TableHead sx={{'& .MuiTableCell-root': {fontWeight: 'bold'}}}>
                <TableRow>
                  <TableCell sx={{ py: 0.5 }}>IREF</TableCell>
                  <TableCell sx={{ py: 0.5 }}>Date</TableCell>
                  <TableCell sx={{ py: 0.5 }}>Location</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(this.state.inventoryChecks || []).map((inv, idx) => (
                  <TableRow key={idx} onClick={() => this.openInventoryDetail(inv)} style={{ cursor: 'pointer' }}>
                    <TableCell sx={{ py: 0.5 }}>{inv.iref || inv.id}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{inv.date}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{inv.location?.name || inv.location?.uid || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>
    );
  };

  renderNewInventoryForm = () => {
    const { inventoryData, productEntries } = this.state;
    let locations = [{ iref: 0, name: 'Select Location' }];
    if (this.state.locations) {
      Object.keys(this.state.locations).forEach(key => {
        let fData = this.state.locations[key];
        let myData = { iref: fData.iref, name: fData.name };
        locations.push(myData);
      });
    }
    const unitTypeMap = { 0: 'pc', 1: 'kg' };

    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');

    const minDateObj = new Date(today);
    minDateObj.setDate(minDateObj.getDate() - 30);
    const yyyyMin = minDateObj.getFullYear();
    const mmMin = String(minDateObj.getMonth() + 1).padStart(2, '0');
    const ddMin = String(minDateObj.getDate()).padStart(2, '0');
    const minDate = `${yyyyMin}-${mmMin}-${ddMin}`;

    const maxDate = `${yyyy}-${mm}-${dd}`;

    return (
      <Paper variant="outlined" sx={compactPaperSx}>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Grid
            container
            spacing={1}
            sx={{
              flexShrink: 0,
              mb: 0.75,
              alignItems: 'center',
            }}
          >
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Select Date"
                  value={inventoryData.date ? dayjs(inventoryData.date) : null}
                  onChange={(newValue) => {
                    const formattedDate = newValue ? newValue.format('YYYY-MM-DD') : '';
                    this.handleInventoryChange('date', formattedDate);
                  }}
                  minDate={dayjs(minDate)}
                  maxDate={dayjs(maxDate)}
                  inputFormat="DD.MM.YYYY"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      sx={{
                        ...compactFieldSx,
                        width: '100%',
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  required
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                id="standard-select-location"
                label="Select Location"
                select
                required
                defaultValue="0"
                variant="standard"
                sx={{
                  ...compactFieldSx,
                  width: '100%',
                  pt: 0,
                }}
                onChange={this.selectLocation}
              >
                {locations.map((option) => (
                  <MenuItem key={option.iref} value={option.iref}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <TableContainer component={Paper} sx={compactTableContainerSx}>
            <Table
              stickyHeader
              size="small"
              sx={{
                ...compactTableSx,
                tableLayout: 'fixed',
                width: '100%',
              }}
            >
              <TableHead sx={{'& .MuiTableCell-root': {fontWeight: 'bold'}}}>
                <TableRow>
                  <TableCell sx={{ py: 0.5, width: '50%', textAlign: 'left' }}>Product</TableCell>
                  <TableCell sx={{ py: 0.5, width: '20%', textAlign: 'center' }}>Unit</TableCell>
                  <TableCell sx={{ py: 0.5, width: '30%', textAlign: 'right' }}>Quantity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productEntries.map((entry, index) => (
                  <TableRow key={entry.productId}>
                    <TableCell sx={{ py: 0.5, width: '50%', textAlign: 'left' }}>{entry.productName}</TableCell>
                    <TableCell sx={{ py: 0.5, width: '20%', textAlign: 'center' }}>{unitTypeMap[entry.unit]}</TableCell>
                    <TableCell sx={{ py: 0.5, width: '30%', textAlign: 'right' }}>
                      <TextField
                        type="text"
                        value={String(entry.quantity ?? '')}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const normalized = raw.replace(',', '.');

                          if (Number(entry.unit) === 1) {
                            // allow - , optional dot, up to 3 decimals, allow trailing dot
                            if (/^-?\d*(?:\.\d{0,3})?$/.test(normalized)) {
                              this.handleQuantityChange(index, normalized);
                            }
                          } else {
                            // integers only
                            if (/^-?\d*$/.test(normalized)) {
                              this.handleQuantityChange(index, normalized);
                            }
                          }
                        }}
                        onBlur={(e) => {
                          let v = String(e.target.value ?? '').replace(',', '.').trim();

                          if (v === '' || v === '-') {
                            this.handleQuantityChange(index, '');
                            return;
                          }

                          // If it’s not a number, revert/ignore
                          if (!/^[-]?\d+(\.\d+)?$/.test(v)) return;

                          if (Number(entry.unit) === 1) {
                            // clamp to 3 decimals (important for paste)
                            const [i, d = ''] = v.split('.');
                            const out = d.length ? `${i}.${d.slice(0, 3)}` : i;
                            this.handleQuantityChange(index, out);
                          } else {
                            this.handleQuantityChange(index, String(parseInt(v, 10)));
                          }
                        }}
                        inputProps={{
                          inputMode: Number(entry.unit) === 1 ? 'decimal' : 'numeric',
                          style: { textAlign: 'right' },
                        }}
                        InputProps={{
                          sx: {
                            py: 0.2,
                            height: 30,
                            width: {
                              xs: 76,
                              sm: 110,
                            },
                            fontSize: {
                              xs: '16px',
                              sm: '0.84rem',
                            },
                            backgroundColor: entry.unit === 1 ? '#e6f7ff' : 'inherit',
                          },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Grid
            container
            alignItems="center"
            spacing={1}
            sx={{
              flexShrink: 0,
              mt: 0.75,
              mb: 0,
              '& .MuiButton-root': {
                height: 34,
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 750,
              },
            }}
          >
            <Grid item xs={12} sm={2} />
              <Grid item xs={12} sm={8} sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    minWidth: {
                      xs: '100%',
                      sm: 200,
                    },
                    px: 3,
                  }}
                  onClick={this.handleSubmit}
                >
                  Save Inventory Check
                </Button>
              </Grid>

              <Grid item xs={12} sm={2} sx={{ textAlign: { xs: 'center', sm: 'right' }, pr: { xs: 0, sm: 1 } }}>
                <Button
                  variant="outlined"
                  sx={{
                    minWidth: {
                      xs: '100%',
                      sm: 80,
                    },
                    px: 3,
                  }}
                  onClick={this.handleExportProductsPDF}
                  startIcon={<FileDownloadIcon />}
                >
                  List
                </Button>
              </Grid>
          </Grid>
        </Box>
      </Paper>
    );
  };

  renderInventoryDetailDialog = () => {
    const { selectedInventory } = this.state;
    if (!selectedInventory) return null;
    const existingIds = new Set(
      (selectedInventory.inventoryCheckLines_invCheck || []).map(l => String(l.product))
    );
    const availableProducts = (this.state.productEntries || [])
      .filter(p => !existingIds.has(String(p.productId)))
      .sort((a, b) => a.productName.localeCompare(b.productName));

    return (
      <Dialog open={this.state.showDetailDialog} onClose={this.closeInventoryDetail} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Inventory Detail - {selectedInventory.iref || selectedInventory.id}
          <Button
            color="inherit"
            size="small"
            onClick={this.onDownload}
            variant="contained"
            style={{ maxWidth: '36px', minWidth: '36px', marginLeft: 8 }}
          >
            <Avatar sx={{ width: 24, height: 24, bgcolor: theme.palette.success.light }}>
              <FileDownloadIcon />
            </Avatar>
          </Button>
          <IconButton
            aria-label="close"
            onClick={this.closeInventoryDetail}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box display="flex" justifyContent="flex-start" alignItems="center" sx={{ pl: 2, gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
                <DatePicker
                  label="Inventory date"
                  value={this.state.editDate ? dayjs(this.state.editDate) : null}
                  onChange={(newValue) => {
                    const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
                    this.setState({ editDate: formatted });
                  }}
                  inputFormat="DD.MM.YYYY"
                  disabled={!this.state.editMode}
                  renderInput={(params) => (
                    <TextField {...params} variant="standard" sx={{ width: 180 }} InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Box>
            </LocalizationProvider>
            {!this.state.editMode ? (
              <Button onClick={() => this.setState({ editMode: true })} variant="contained" size="small">Edit</Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={() => this.setState({ editMode: false, editDate: this.state.selectedInventory?.date || '' })}
                  variant="text"
                  size="small"
                >
                  Cancel
                </Button>
                <Button onClick={this.handleSaveDate} variant="contained" size="small" disabled={this.state.isSavingDate}>
                  {this.state.isSavingDate ? 'Saving…' : 'Save'}
                </Button>
              </Box>
            )}
            {this.state.editMode && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => this.setState({ showAddProductDialog: true, addProductId: '', addQty: '' })}
                disabled={!availableProducts.length}
              >
                Add product
              </Button>
            )}
          </Box>
          <Button onClick={this.closeInventoryDetail} variant="text" size="small">Close</Button>
        </DialogActions>
        <DialogContent>
          <Typography>Location: {selectedInventory.location?.name || '-'}</Typography>

          {selectedInventory.inventoryCheckLines_invCheck?.length > 0 && (
            <Box mt={2} sx={{ maxHeight: '55vh', overflow: 'auto' }}>
              <Table stickyHeader size="small" sx={{ ...compactTableSx, tableLayout: 'fixed', width: '100%' }}>
                <TableHead sx={{'& .MuiTableCell-root': {fontWeight: 'bold'}}}>
                  <TableRow>
                    <TableCell sx={{ py: 0.5 }}>Product</TableCell>
                    <TableCell sx={{ py: 0.5, width: 80, textAlign: 'center' }}>Unit</TableCell>
                    <TableCell sx={{ py: 0.5, width: 140, textAlign: 'right' }}>Quantity</TableCell>
                    {!this.state.editMode && (
                      <TableCell sx={{ py: 0.5, width: 120, textAlign: 'right' }}>Action</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedInventory.inventoryCheckLines_invCheck.map((line, idx) => {
                    const pid = (line.product && typeof line.product === 'object') ? line.product.iref : line.product;
                    const rowKey = String(line.iref ?? line.line_iref ?? line.id ?? pid ?? '');
                    const isEditing = !!this.state.editingLineIds[rowKey];
                    const currentVal = this.state.lineEdits[rowKey] ?? line.quantity ?? '';
                    const pe = this.state.productEntries.find(p => String(p.productId) === String(pid));
                    const unit = (line.product && typeof line.product === 'object')
                      ? line.product.unitType
                      : (pe ? pe.unit : undefined);
                    const productName =
                      pe?.productName ||
                      ((line.product && typeof line.product === 'object') ? line.product.name : null) ||
                      String(pid || '-');
                    return (
                      <TableRow key={idx}>
                        <TableCell sx={{ py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{productName}</TableCell>
                        <TableCell sx={{ py: 0.5, width: 80, textAlign: 'center' }}>{unit === 0 ? 'pc' : unit === 1 ? 'kg' : '-'}</TableCell>
                        <TableCell sx={{ py: 0.5, width: 140, textAlign: 'right' }}>
                          {this.state.editMode ? (
                            <Typography sx={{ textAlign: 'right' }}>{line.quantity}</Typography>
                          ) : isEditing ? (
                            <TextField
                              type="text"
                              size="small"
                              value={currentVal}
                              onChange={(e) => {
                                const val = e.target.value.replace(',', '.');
                                if (unit === 0) {
                                  if (/^\d*$/.test(val)) this.handleLineQtyChange(rowKey, val);
                                } else {
                                  if (/^\d*\.?\d{0,3}$/.test(val)) this.handleLineQtyChange(rowKey, val);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  this.saveLineQty(line, unit);
                                }
                              }}
                              inputProps={{
                                inputMode: unit === 0 ? 'numeric' : 'decimal',
                                style: { textAlign: 'right' }
                              }}
                              InputProps={{
                                sx: {
                                  py: 0.2,
                                  height: '32px',
                                  width: '120px',
                                  fontSize: '1rem',
                                  backgroundColor: unit === 1 ? '#e6f7ff' : 'inherit'
                                }
                              }}
                            />
                          ) : (
                            <Typography sx={{ textAlign: 'right' }}>{line.quantity}</Typography>
                          )}
                        </TableCell>
                        {!this.state.editMode && (
                          <TableCell sx={{ py: 0.5, width: 120, textAlign: 'right' }}>
                            {!isEditing ? (
                              <IconButton onClick={() => this.toggleEditLine(rowKey, line.quantity)} size="small" aria-label="Edit quantity">
                                <EditIcon fontSize="small" />
                              </IconButton>
                            ) : (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => this.saveLineQty(line, unit)}
                                disabled={!!this.state.savingLineIds[rowKey || pid]}
                              >
                                {this.state.savingLineIds[rowKey || pid] ? 'Saving…' : 'Save'}
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <Dialog
          open={this.state.showAddProductDialog}
          onClose={() => this.setState({ showAddProductDialog: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add product</DialogTitle>

          <DialogContent sx={{ pt: 1 }}>
            <TextField
              select
              fullWidth
              label="Product"
              variant="standard"
              value={this.state.addProductId}
              onChange={(e) => this.setState({ addProductId: e.target.value })}
              disabled={!availableProducts.length}
            >
              {availableProducts.map(p => (
                <MenuItem
                  key={p.productId}
                  value={p.productId}
                  sx={{
                    backgroundColor: Number(p.unit) === 1 ? '#e6f7ff' : 'inherit',
                    '&.Mui-selected': { backgroundColor: Number(p.unit) === 1 ? '#d6f0ff' : 'inherit' },
                    '&.Mui-selected:hover': { backgroundColor: Number(p.unit) === 1 ? '#cbeaff' : 'inherit' },
                  }}
                >
                  {p.productName}
                </MenuItem>
              ))}
            </TextField>

            {(() => {
              const p = (this.state.productEntries || []).find(x => String(x.productId) === String(this.state.addProductId));
              const unit = p?.unit;

              return (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <TextField
                    fullWidth
                    label="Quantity"
                    variant="standard"
                    sx={{
                      mt: 2,
                      width: 140,
                      backgroundColor: Number(unit) === 1 ? '#e6f7ff' : 'inherit', // 👈 ADD
                    }}
                    value={this.state.addQty}
                    onChange={(e) => {
                      const normalized = e.target.value.replace(',', '.');

                      if (Number(unit) === 1) {
                        if (/^-?\d*(?:\.\d{0,3})?$/.test(normalized)) this.setState({ addQty: normalized });
                      } else {
                        if (/^-?\d*$/.test(normalized)) this.setState({ addQty: normalized });
                      }
                    }}
                    onBlur={(e) => {
                      let v = String(e.target.value ?? '').replace(',', '.').trim();
                      if (v === '' || v === '-') return;

                      if (!/^[-]?\d+(\.\d+)?$/.test(v)) return;

                      if (Number(unit) === 1) {
                        const [i, d = ''] = v.split('.');
                        this.setState({ addQty: d ? `${i}.${d.slice(0, 3)}` : i });
                      } else {
                        this.setState({ addQty: String(parseInt(v, 10)) });
                      }
                    }}
                    inputProps={{
                      inputMode: Number(unit) === 1 ? 'decimal' : 'numeric',
                      style: { textAlign: 'right' }
                    }}
                  />
                </Box>
              );
            })()}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => this.setState({ showAddProductDialog: false })}>Cancel</Button>

            <Button
              variant="contained"
              disabled={
                !this.state.addProductId ||
                this.state.isAddingLine ||
                this.state.addQty === '' ||
                this.state.addQty === '-' ||
                Number.isNaN(Number(this.state.addQty))
              }
              onClick={this.addProductLineToInventory}
            >
              {this.state.isAddingLine ? 'Adding…' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>
    );
  };

  addProductLineToInventory = () => {
    const { selectedInventory, addProductId, addQty } = this.state;
    if (!selectedInventory || !addProductId) return;

    const v = String(addQty ?? '').replace(',', '.').trim();
    if (v === '' || v === '-') {
      alert('Quantity is required.');
      return;
    }
    const qty = Number(v);
    if (Number.isNaN(qty)) {
      alert('Invalid quantity.');
      return;
    }

    // extra duplicate guard
    const existingIds = new Set(
      (selectedInventory.inventoryCheckLines_invCheck || []).map(l =>
        String((l.product && typeof l.product === 'object') ? l.product.iref : l.product)
      )
    );
    if (existingIds.has(String(addProductId))) {
      alert('This product is already in the inventory list.');
      return;
    }

    const base = Config.API_URL_INVENTORY_CHECK;
    const url = `${base}${base.endsWith('/') ? '' : '/'}add-line/`;

    this.setState({ isAddingLine: true });
    const payload = { iref: selectedInventory.iref, product: addProductId, quantity: qty };
    const res = this.state.client.pushData(url, payload);
    this.setState({ isAddingLine: false });

    if (!res) {
      alert('Add failed.');
      return;
    }

    const newLine = res.line || res;
    const pid =
      (newLine.product && typeof newLine.product === 'object')
        ? newLine.product.iref
        : (newLine.product ?? newLine.product_id ?? newLine.productId);
    const lineIref = newLine.iref ?? newLine.line_iref ?? newLine.id;
    const normalizedLine = {
      ...newLine,
      // ✅ ensure these exist in the shape your UI editing uses
      iref: lineIref,
      product: pid,
      // keep qty visible
      quantity: (newLine.quantity ?? qty),
      // optional: keep full product if you want
      productDetails: (newLine.product && typeof newLine.product === 'object') ? newLine.product : newLine.productDetails,
    };
    this.setState(prev => ({
      selectedInventory: {
        ...prev.selectedInventory,
        inventoryCheckLines_invCheck: [...(prev.selectedInventory.inventoryCheckLines_invCheck || []), normalizedLine]
      },
      inventoryChecks: (prev.inventoryChecks || []).map(inv =>
        String(inv.iref || inv.id) === String(prev.selectedInventory.iref)
          ? { ...inv, inventoryCheckLines_invCheck: [...(inv.inventoryCheckLines_invCheck || []), normalizedLine] }
          : inv
      ),
      showAddProductDialog: false,
      addProductId: '',
      addQty: '',
    }));
  };

  render() {
    const { tabIndex, tabs } = this.state;

    return (
      <Box sx={compactPageSx}>
        <Paper elevation={0} sx={compactTabPaperSx}>
          <Tabs
            value={tabIndex}
            onChange={this.handleChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: {
                xs: 36,
                sm: 40,
              },
              '& .MuiTabs-indicator': {
                height: 2,
              },
              '& .MuiTab-root': {
                minHeight: {
                  xs: 36,
                  sm: 40,
                },
                textTransform: 'none',
                fontWeight: 700,
                fontSize: {
                  xs: '0.75rem',
                  sm: '0.84rem',
                },
                px: {
                  xs: 1,
                  sm: 1.4,
                },
              },
            }}
          >
            {tabs.map((label, idx) => (
              <Tab key={idx} label={label} />
            ))}
          </Tabs>
        </Paper>

        <Box sx={compactPanelSx}>
          {tabIndex === 0 ? this.renderInventoryList() : this.renderNewInventoryForm()}
        </Box>

        {this.renderInventoryDetailDialog()}
      </Box>
    );
  }
}

export default InventoryUI;
